import React, { useState, useEffect, useCallback } from 'react';

interface ImageGalleryProps {
  images: string[];
  interval?: number; // Time in ms between transitions
  className?: string;
  altText?: string;
  objectFit?: 'cover' | 'contain' | 'fill' | 'scale-down' | 'none';
  /**
   * Seitenverhältnis, das der Rahmen von Anfang an reserviert (z. B. '1 / 1').
   * Ohne feste Höhe hatte die Galerie im contain-Modus `height: auto`: vor dem
   * Laden war sie 0 px hoch, danach bis zu 600 px — und bei jedem Bildwechsel
   * änderte sie die Höhe erneut. Beides verschiebt alles darunter (CLS).
   */
  aspectRatio?: string;
  /** Bild sofort laden statt lazy — für Galerien above the fold. */
  priority?: boolean;
}

const ImageGallery: React.FC<ImageGalleryProps> = ({ 
  images, 
  interval = 5000, // Default 5 seconds
  className = "",
  altText = "SeaDent Mallorca Gallery",
  objectFit = "cover",
  aspectRatio = "1 / 1",
  priority = false
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Function to go to next slide
  const goToNext = useCallback(() => {
    if (images.length <= 1) return;
    
    setIsTransitioning(true);
    
    // After the fade out completes, switch to the next image
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
      setIsTransitioning(false);
    }, 700); // This should match the CSS transition duration
  }, [images.length]);
  
  // Function to go to specific slide
  const goToSlide = (index: number) => {
    if (index === currentIndex) return;
    
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex(index);
      setIsTransitioning(false);
    }, 700);
  };
  
  // Auto-advance slides
  useEffect(() => {
    if (images.length <= 1) return;
    
    const timer = setInterval(goToNext, interval);
    return () => clearInterval(timer);
  }, [currentIndex, images.length, interval, goToNext]);
  
  if (images.length === 0) return null;
  
  return (
    <div
      className={`relative overflow-hidden rounded-lg shadow-xl ${className} group`}
      style={
        // Nur im contain-Modus: der Rahmen bekommt keine feste Höhe über
        // className, also reservieren wir den Platz selbst.
        objectFit === 'contain'
          ? { aspectRatio, maxHeight: '600px', marginInline: 'auto' }
          : undefined
      }
    >
      {/* Main image with smooth transition - now using actual img tag instead of background-image */}
      <img 
        src={images[currentIndex]}
        alt={`${altText} ${currentIndex + 1}`}
        className={`w-full h-full transition-all duration-700 ease-in-out ${
          isTransitioning 
            ? 'opacity-0 transform scale-105 blur-sm' 
            : 'opacity-100 transform scale-100 blur-0'
        }`}
        style={{ 
          height: '100%', 
          width: '100%', 
          objectFit: objectFit
        }}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
      />
      
      {/* Overlay to enhance image and text contrast */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
      
      {/* Navigation dots */}
      <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-3">
        {images.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-3 h-3 rounded-full transition-all duration-500 ${
              currentIndex === index 
                ? 'bg-white scale-100 opacity-100' 
                : 'bg-white/50 scale-75 opacity-70 hover:opacity-100 hover:scale-90'
            }`}
            aria-label={`${altText} ${index + 1}`}
          />
        ))}
      </div>
      
      {/* Navigation arrows - hidden by default, visible on hover */}
      <button 
        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/50 transition-all duration-300 rounded-full p-2 text-white opacity-0 group-hover:opacity-100 transform -translate-x-2 group-hover:translate-x-0"
        onClick={() => {
          setIsTransitioning(true);
          setTimeout(() => {
            setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
            setIsTransitioning(false);
          }, 700);
        }}
        aria-label="Previous image"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
      
      <button 
        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/50 transition-all duration-300 rounded-full p-2 text-white opacity-0 group-hover:opacity-100 transform translate-x-2 group-hover:translate-x-0"
        onClick={goToNext}
        aria-label="Next image"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );
};

export default ImageGallery; 